'use client'
import Image from 'next/image'
import { cn } from '@/utils/twMerge'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicRichText, PrismicLink } from '@prismicio/react'

type DataObject = Record<string, any>

type CtaComponentProps = {
  data: DataObject
  type?: string
  uid?: string
  marketingUid?: string
}

const CtaComponent = ({ data, type, uid, marketingUid }: CtaComponentProps) => {
  const getContentClasses = (type?: string): string => {
    const baseClasses = 'flex flex-col items-center justify-between gap-10 rounded-md p-6 md:flex-row md:gap-0'

    if (type === 'supportMetrics') {
      return `${baseClasses} bg-primary-ink-100 px-6 py-8`
    }
    return `${baseClasses} bg-primary-green-85`
  }

  const getHeadingClasses = (type?: string): string => {
    // Green CTA (`bg-primary-green-85`) needs dark ink for WCAG AA contrast;
    // dark surfaces (supportMetrics) keep white headings.
    const headingColor = type === 'supportMetrics' ? '[&_h3]:text-white' : '[&_h3]:text-primary-ink-100'
    const baseClasses = `${headingColor} [&_em]:block [&_em]:not-italic [&_em]:lg:-mt-3 text-center md:text-left`

    if (type === 'blog') {
      return `${baseClasses} [&_h3]:!text-4xl [&_h3]:!mb-0`
    }
    if (type === 'supportMetrics') {
      return `${baseClasses} [&_em]:inline-block [&_em]:not-italic [&_em]:lg:-mt-3 [&_strong]:font-medium [&_strong]:text-primary-green-150`
    }
    if (type === 'nistCta') {
      return `${baseClasses} [&_em]:inline-block [&_em]:not-italic [&_em]:lg:-mt-3 [&_strong]:font-medium [&_strong]:text-primary-ink-100`
    }
    if (type === 'companySmallCTA') {
      return `${baseClasses} [&_em]:block [&_em]:not-italic [&_em]:text-primary-ink-100 [&_em]:lg:-mt-3 [&_strong]:font-medium [&_strong]:text-primary-ink-100`
    }
    return `${baseClasses} [&_strong]:font-medium [&_strong]:text-primary-ink-100`
  }

  const getContainerClasses = (type?: string): string => {
    if (['blog', 'customer', 'hub'].includes(type as string)) {
      return '!p-0'
    }
    return ''
  }

  const getSectionClasses = (type?: string, uid?: string): string => {
    const baseClasses = 'relative z-[1] overflow-hidden pb-[140px]'
    const noPbUids = ['cyber-essentials', 'iso-9001', 'cjis', 'ftc-safeguards-rule', 'nydfs']

    if (['blog', 'customer'].includes(type ?? '')) return `${baseClasses} py-8 !pb-0`
    if (noPbUids.includes(uid ?? '')) return `${baseClasses} !pb-0`
    if (type === 'hub') return 'py-6'
    if (marketingUid === 'cmmc') return `${baseClasses} bg-primary-ink-100`
    if (['request-cmmc-demo'].includes(marketingUid as string)) return `${baseClasses} bg-primary-ink-100 !pb-0`
    return baseClasses
  }

  return (
    <section className={cn(`${type}-cta`, getSectionClasses(type, uid))}>
      <div className={`container ${getContainerClasses(type)}`}>
        <div className={`relative ${getContentClasses(type)}`}>
          <div className={getHeadingClasses(type)}>
            <PrismicRichText field={data.primary.title} />
          </div>

          {type === 'supportMetrics' && (
            <Image
              src="/images/g2_badges.png"
              width={400}
              height={120}
              className="h-[120px] w-auto object-contain"
              alt="G2 badges"
              loading="lazy"
            />
          )}

          {type === 'nistCta' && (
            <PrismicNextImage
              field={data.primary.logo}
              width={400}
              height={120}
              className="h-[120px] w-auto object-contain"
              loading="lazy"
              fallbackAlt=""
            />
          )}

          {['service-partner-signup', 'service-partner', 'request-cmmc-demo', 'summit'].includes(uid as string) ? (
            <button
              className="btn btn-primary btn-small btn-interaction relative z-[1]"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              {data.primary.btn_label}
              <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
            </button>
          ) : (
            <PrismicLink
              field={data.primary.btn_link}
              className={cn(
                'btn btn-primary btn-small btn-interaction relative z-[1]',
                type === 'supportMetrics' ? 'btn-secondary bg-white text-primary-ink-100' : '',
                ['blog', 'customer'].includes(type || '') ? '!text-white !no-underline' : '',
              )}>
              {data.primary.btn_label}
              <Image
                src={type === 'supportMetrics' ? '/images/angle-right-black.svg' : '/images/angle-right-green.svg'}
                width={20}
                height={16}
                alt=""
                aria-hidden="true"
              />
            </PrismicLink>
          )}

          {/* Background Image - positioned relative to content container */}
          <div
            className="absolute right-0 top-0 z-[0] hidden overflow-hidden lg:block"
            style={{ maxWidth: '50%', height: '100%' }}>
            <Image
              src="/images/cta-bg.svg"
              alt="cta-bg"
              width={0}
              height={0}
              sizes="50vw"
              style={{ width: 'auto', height: '100%', maxWidth: '100%' }}
              className="object-contain object-right-top"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default CtaComponent
